import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button.js';
import infoIcon from '../assets/info-icon.svg';

/**
 * Route-level React error boundary (issues #276 / #279).
 *
 * The SPA previously mounted every routed screen UNGUARDED inside `<Routes>`
 * (`App.tsx`). React's contract is that an uncaught exception thrown during
 * render unmounts the ENTIRE component tree — so a single unexpected data shape
 * reaching any screen (e.g. a real-gateway `EPortalResponseContent` field that
 * arrives in an unanticipated form on a Statements sub-option) blanked the whole
 * app to a grey/white page, with the shell gone and no in-app way back. The only
 * recovery was a full browser refresh. Both #276 ("grey screen … page needs to
 * be refreshed") and #279 ("white page … no way to get back") describe exactly
 * this: the missing top-level boundary, not a single crash site (the Statements
 * screen itself is exhaustively null-guarded). #262 fixed one specific
 * `AccountNumber.slice` throw but noted the boundary was never added, so any
 * other render throw still blanked the tree.
 *
 * This boundary wraps the routed screen content INSIDE the app shell, so a render
 * exception now shows a recoverable in-app panel (with the shell/navigation
 * intact and a Back-to-dashboard affordance) instead of unmounting everything.
 * It resets its error state whenever the route (`resetKey`) changes, so simply
 * navigating away — via the surviving shell nav or the Back button — recovers the
 * app WITHOUT a browser refresh, directly addressing "no way to get back".
 *
 * This is defence-in-depth: individual screens should still guard their own data
 * (and do). The boundary guarantees that a miss anywhere degrades to a
 * recoverable panel rather than a dead blank page.
 */

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** When this value changes, the boundary clears any captured error. */
  resetKey: string;
  /** Rendered when a descendant throws during render. */
  fallback: (reset: () => void) => React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class ErrorBoundaryInner extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Recover automatically when the agent navigates to a different route: the
    // shell survives the crash, so a route change clears the captured error and
    // re-renders the fresh screen — no browser refresh required (#276 / #279).
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surfaced for live diagnosis (e.g. inside the Genesys iframe console).
    console.error('[route-error-boundary] screen render threw', error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): React.ReactNode {
    if (this.state.error) {
      return this.props.fallback(this.reset);
    }
    return this.props.children;
  }
}

/**
 * Route-scoped error boundary + recoverable fallback panel. Wrap the routed
 * screen content with this inside the app shell.
 */
export function RouteErrorBoundary({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const fallback = (reset: () => void): React.ReactNode => {
    const goToDashboard = (): void => {
      // Clear the captured error first, then navigate: the resetKey change would
      // also clear it, but doing it here makes recovery immediate even if the
      // target route is the same one that threw.
      reset();
      navigate('/dashboard');
    };

    return (
      <main
        className="max-w-[900px] mx-auto px-12 py-12"
        aria-labelledby="screen-error-heading"
      >
        <div className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3">
          <img
            src={infoIcon}
            alt=""
            aria-hidden="true"
            className="w-5 h-5 mt-1 shrink-0"
          />
          <div>
            <h1
              id="screen-error-heading"
              role="alert"
              className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2"
            >
              Something went wrong displaying this page
            </h1>
            <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-4 max-w-[560px]">
              The page could not be displayed. You can go back to the customer
              dashboard and try again — you do not need to refresh your browser.
            </p>
            <Button
              type="button"
              onClick={goToDashboard}
              aria-label="Back to customer dashboard"
              className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
            >
              Back to customer dashboard
            </Button>
          </div>
        </div>
      </main>
    );
  };

  return (
    <ErrorBoundaryInner resetKey={location.pathname} fallback={fallback}>
      {children}
    </ErrorBoundaryInner>
  );
}
